import { useEffect } from 'react'
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api } from './api'
import type { Chat, Topic } from './apiModels'
import { chatTarget } from './chatNavigation'

export type ReadyCodeAgent = 'codex' | 'claude'
type Launch = { agent: ReadyCodeAgent; topicId: string }
type Creation = { id: string; title: string; session_ref?: number | null }
const PREFIX = 'agentchat:ready-code:v1'
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const preparing = new Map<ReadyCodeAgent, Promise<void>>()
const claiming = new Map<string, Promise<Creation>>()
const creationState = new Map<string, 'pending' | 'ready'>()
const chatIdFor = (agent: ReadyCodeAgent) => `pwa-auto-code-${agent}`
const slotKey = (agent: ReadyCodeAgent) => `${PREFIX}:slot:${agent}`
const claimKey = (chatId: string, topicId: string) => `${PREFIX}:claim:${chatId}:${topicId}`

function read(key: string): string | null {
  try { return window.localStorage.getItem(key) } catch { return null }
}
function write(key: string, value: string): boolean {
  try { window.localStorage.setItem(key, value); return true } catch { return false }
}
function newSlot(agent: ReadyCodeAgent): string {
  const saved = read(slotKey(agent))
  if (saved && UUID.test(saved)) return saved
  const id = crypto.randomUUID()
  write(slotKey(agent), id)
  return id
}

export async function warmReadyCodeConversations(): Promise<void> {
  await Promise.allSettled((['codex', 'claude'] as const).map(prepare))
}
function prepare(agent: ReadyCodeAgent): Promise<void> {
  const pending = preparing.get(agent)
  if (pending) return pending
  const id = newSlot(agent)
  const request = api<Creation>(`/api/chats/${chatIdFor(agent)}/topics/ready`, {
    method: 'POST', signal: AbortSignal.timeout(10_000),
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ opening_prompt: '', client_topic_id: id, prepare_only: true }),
  }).then(() => undefined).catch((error: unknown) => {
    // A lost claim response can leave the old local spare already in use.
    if (error instanceof Error && /409|already been opened|no longer available|belongs to another account/.test(error.message) && read(slotKey(agent)) === id) {
      write(slotKey(agent), crypto.randomUUID())
    }
  }).finally(() => {
    preparing.delete(agent)
    const current = read(slotKey(agent))
    if (current && current !== id) void prepare(agent)
  })
  preparing.set(agent, request)
  return request
}

function stateFor(chatId: string, topicId: string): 'pending' | 'ready' | null {
  const key = claimKey(chatId, topicId)
  const memory = creationState.get(key)
  if (memory) return memory
  const saved = read(key)
  return saved === 'ready' || saved === 'pending' ? saved : null
}
export function ensureReadyCodeConversation(
  chatId: string, topicId: string, requested = false,
): Promise<Creation | undefined> {
  if (stateFor(chatId, topicId) === 'ready' || (!requested && stateFor(chatId, topicId) !== 'pending')) {
    return Promise.resolve(undefined)
  }
  const key = claimKey(chatId, topicId)
  const pending = claiming.get(key)
  if (pending) return pending
  creationState.set(key, 'pending')
  write(key, 'pending')
  const request = api<Creation>(`/api/chats/${chatId}/topics`, {
    method: 'POST', signal: AbortSignal.timeout(15_000),
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ opening_prompt: '', client_topic_id: topicId }),
  }).then((created) => {
    creationState.set(key, 'ready')
    write(key, 'ready')
    return created
  }).finally(() => { claiming.delete(key) })
  claiming.set(key, request)
  return request
}

function seedConversation(qc: QueryClient, launch: Launch): void {
  const chatId = chatIdFor(launch.agent)
  if (!qc.getQueryData<Chat>(['chat', chatId])) {
    const existing = qc.getQueryData<Chat[]>(['chats'])?.find((chat) => chat.id === chatId)
    const chat: Chat = existing ?? {
      id: chatId, platform_id: chatId, display_name: `${launch.agent === 'codex' ? 'Codex' : 'Claude'} Code`,
      type: 'auto-spawn', category: 'coding', agent: launch.agent, lane_id: null, todoist_task_id: null,
      pinned: false, archived: false, last_activity_at: null, created_at: Date.now() / 1000,
      lane_status: 'idle', lane_last_heartbeat: null, active_work_depth: 0,
    }
    qc.setQueryData(['chat', chatId], chat)
  }
  const topic: Topic = {
    id: launch.topicId, title: 'New conversation', position: 0, snoozed_until: null,
    is_snoozed: false, closed: false, closed_at: null, created_at: Date.now() / 1000,
    preferred_model: launch.agent, status: 'idle', unread: 0,
  }
  qc.setQueryData<Topic[]>(['topics', chatId], (current) => current?.some((row) => row.id === topic.id)
    ? current : [...(current ?? []), topic])
  if (!qc.getQueryData(['code-conversation', chatId, launch.topicId])) {
    qc.setQueryData(['code-conversation', chatId, launch.topicId], {
      mode: 'chat', status: 'idle', error: null, stoppable: false,
    })
  }
}

function reserve(agent: ReadyCodeAgent, launchId: string, useSpare: boolean): Launch {
  const key = `${PREFIX}:launch:${launchId}`
  const saved = read(key)
  if (saved) {
    try {
      const value: unknown = JSON.parse(saved)
      if (typeof value === 'object' && value !== null && 'agent' in value && 'topicId' in value
        && value.agent === agent && typeof value.topicId === 'string' && UUID.test(value.topicId)) {
        return { agent, topicId: value.topicId }
      }
    } catch { /* A damaged mapping falls back to the stable per-tap UUID. */ }
    return { agent, topicId: launchId }
  }
  let topicId = useSpare ? newSlot(agent) : launchId
  // Rotate before recording the mapping. Storage failure uses the per-tap ID,
  // so separate launches can never accidentally reuse the same spare.
  if (!write(slotKey(agent), crypto.randomUUID()) || !write(key, JSON.stringify({ agent, topicId }))) {
    topicId = launchId
  }
  return { agent, topicId }
}

export async function openReadyCodeConversation(
  qc: QueryClient, agent: ReadyCodeAgent, launchId: string, parentTab: 'code' | 'lanes' = 'code',
): Promise<string> {
  if (!UUID.test(launchId)) throw new Error('Invalid Code shortcut')
  const launch = typeof navigator !== 'undefined' && navigator.locks
    ? await navigator.locks.request(`${PREFIX}:${agent}`, () => reserve(agent, launchId, true))
    : reserve(agent, launchId, false)
  seedConversation(qc, launch)
  // These requests run after the destination is selected; neither is on the tap path.
  void ensureReadyCodeConversation(chatIdFor(agent), launch.topicId, true).catch(() => {})
  void prepare(agent)
  return `${chatTarget(chatIdFor(agent), launch.topicId, { parentTab })}&focus_composer=1&code_launch=1`
}

export function useReadyCodeCreation(chatId: string, topicId: string, requested: boolean) {
  const qc = useQueryClient()
  const creation = useQuery({
    queryKey: ['code-ready-creation', chatId, topicId],
    queryFn: async () => (await ensureReadyCodeConversation(chatId, topicId, requested)) ?? null,
    enabled: stateFor(chatId, topicId) !== 'ready' && (requested || stateFor(chatId, topicId) === 'pending'),
    retry: false, staleTime: Infinity, refetchOnWindowFocus: false, networkMode: 'always',
  })
  useEffect(() => {
    if (!creation.data) return
    const created = creation.data
    qc.setQueriesData<Topic[]>({ queryKey: ['topics', chatId] }, (current) => current?.map((topic) => (
      topic.id === topicId ? { ...topic, title: created.title, session_ref: created.session_ref } : topic
    )))
    void qc.invalidateQueries({ queryKey: ['topics', chatId] })
  }, [chatId, creation.data, qc, topicId])
  return creation
}
